import { get } from 'svelte/store';
import { WindowsCustomEventTypes } from '../enums/windows-custom-event.enum';
import { WindowIntegrations } from '../helpers/window-integrations';
import { state_localStorage, state_sessionStorage } from '../store/state';
import { defaultLogger as logger } from '../telemetry/logger';
import { AnswerHelper } from './handlers/answer.handler';
import { ConfigHandler } from './handlers/config.handler';
import { FaqHelper } from './handlers/faq.handler';
import { RatingHelper } from './handlers/rating.handler';
import { SuggestionHandler } from './handlers/suggestion.handler';
import { SessionStoreHandler } from '../helpers/session-storage-handler';
import { ConversationHistoryHelper } from '../helpers/conversation-history-helper';
import { ThreadHandler } from './handlers/thread.handler';
import { ConfigStore } from '../store/config';
import { LocalStoreHandler } from '../helpers/local-storage-handler';
import type { IRequestSearchFaq } from '../interfaces/request-search-faq.interface';
import type { IRequestSearch } from '../interfaces/request-search-question.interface';
import type { IUpdateRating } from '../interfaces/request-update-rating-question.interface';
import type { IRequestSearchSuggestion } from '../interfaces/request-search-suggestion.interface';
import type { IRequestOption } from '../interfaces/request-option.interface';
import { UserHandler } from './handlers/user.handler';

export class SearchController {
  private configHandler: ConfigHandler;
  private getSuggestionHandler: SuggestionHandler;
  private getAnswerHelper: AnswerHelper;
  private getFaqHelper: FaqHelper;
  private ratingHelper: RatingHelper;
  private threadHandler: ThreadHandler;
  private sessionResetTimer: any;
  private userHandler: UserHandler;

  constructor() {
    WindowIntegrations();

    this.configHandler = new ConfigHandler();
    this.getSuggestionHandler = new SuggestionHandler();
    this.getAnswerHelper = new AnswerHelper();
    this.getFaqHelper = new FaqHelper();
    this.ratingHelper = new RatingHelper();
    this.threadHandler = new ThreadHandler();
    this.userHandler = new UserHandler();

    this.listeners();
    this.initReady();
    this.initStorage();
  }

  /** send window event after controller is ready */
  private initReady(): void {
    window.dispatchEvent(new CustomEvent(WindowsCustomEventTypes.ready));
  }

  private listeners(): void {
    const _this = this;

    /** check for type of action */
    function triggerAction(
      e: CustomEvent<
        IRequestSearch | IUpdateRating | IRequestSearchFaq | IRequestOption
      >
    ): void {
      try {
        logger.debug('SearchController/triggerAction ', e.type);

        // reset session timout
        logger.debug(
          'Session inactivity timer is = ' +
            get(ConfigStore).sessionInactivityTimeout +
            ' seconds'
        );
        clearTimeout(_this.sessionResetTimer);
        if ((get(ConfigStore).sessionInactivityTimeout ?? 0) > 0) {
          logger.debug(
            'Session inactivity timer (re-)started to ' +
              get(ConfigStore).sessionInactivityTimeout +
              ' seconds'
          );
          const handler: TimerHandler = () => {
            logger.debug('Session inactivity timeout triggered');
            if (SessionStoreHandler.get()?.visible) {
              // only if widget is visible
              // thread reset
              _this.threadHandler.clear();
              _this.requestGreeting();
              // reset CSAT
              window.dispatchEvent(
                new CustomEvent(WindowsCustomEventTypes.showCsatPopup, {
                  detail: {
                    isRated: false,
                    isNotification: false,
                    lengthOfQuestions: 0,
                  },
                })
              );
              _this.sessionResetTimer = setTimeout(
                handler,
                (get(ConfigStore).sessionInactivityTimeout ?? 100) * 1000
              );
            }
          };
          _this.sessionResetTimer = setTimeout(
            handler,
            (get(ConfigStore).sessionInactivityTimeout ?? 100) * 1000
          );
        }

        switch (e.type) {
          case WindowsCustomEventTypes.requestSearch:
            _this.getAnswerHelper.get(e.detail as IRequestSearch);
            break;

          case WindowsCustomEventTypes.updateRating:
            _this.ratingHelper.update(e.detail as IUpdateRating);
            break;

          case WindowsCustomEventTypes.requestSuggestion:
            _this.getSuggestionHandler.get(
              e.detail as IRequestSearchSuggestion
            );
            break;

          case WindowsCustomEventTypes.requestThread:
            _this.threadHandler.get();
            break;

          case WindowsCustomEventTypes.restartThread:
            _this.threadHandler.clear();
            _this.requestGreeting();
            break;

          case WindowsCustomEventTypes.requestFAQ:
            _this.getFaqHelper.get(e.detail as IRequestSearchFaq);
            break;

          case WindowsCustomEventTypes.requestPostOption:
            _this.configHandler.get(e?.detail as IRequestOption);
            break;

          case WindowsCustomEventTypes.requestGreeting:
            _this.requestGreeting();
            break;

          case WindowsCustomEventTypes.toggleView:
            _this.toggleInit();
            break;
          default:
            break;
        }
      } catch (err) {
        logger.warn('SearchController/triggerAction ', err);
      }
    }

    /** listen to custom events */
    [
      WindowsCustomEventTypes.requestSearch,
      WindowsCustomEventTypes.updateRating,
      WindowsCustomEventTypes.requestSuggestion,
      WindowsCustomEventTypes.requestThread,
      WindowsCustomEventTypes.restartThread,
      WindowsCustomEventTypes.requestFAQ,
      WindowsCustomEventTypes.requestServerConfig,
      WindowsCustomEventTypes.requestServerInit,
      WindowsCustomEventTypes.requestPostOption,
      WindowsCustomEventTypes.requestGreeting,
      WindowsCustomEventTypes.toggleView,
    ].forEach((evt) =>
      window.addEventListener(evt, triggerAction as EventListener)
    );
  }

  private toggleInit(): void {
    // if (ConversationHistoryHelper.isEmpty()) {
    //   this.indicesHelper.get();
    // }
  }

  private initStorage(): void {
    const currentSession = SessionStoreHandler.get();
    state_sessionStorage.set(currentSession);
    const localStorage = LocalStoreHandler.get();
    state_localStorage.set(localStorage);
  }

  public async requestGreeting(): Promise<void> {
    if (get(ConfigStore).loadThreadHistory) {
      await this.threadHandler.get();
    }

    if (!get(ConfigStore).noGreeting) {
      const mergedAttributes = this.userHandler.mergeWithCustomAttributes(
        get(ConfigStore).customAttributes?.attributes
      );

      await this.getAnswerHelper.get({
        query: undefined,
        requestType: 'greeting',
        attributes: mergedAttributes,
        cached_query: get(ConfigStore).customAttributes?.cachedQuery,
        flags: get(ConfigStore).customAttributes?.flags,
        cookies: get(ConfigStore).customAttributes?.cookies,
        url: get(ConfigStore).customAttributes?.url,
      });
    }
  }
}
